import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Descriptions, Modal, Select, Space, Steps, Tag, Tooltip, Typography, theme } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import apiClient from '../../api/client';
import { STATUS_COLORS, STEP_DESCRIPTIONS, STEP_LABELS } from './statusVisuals';

const { Text } = Typography;

export interface QuickCheckTarget {
  // The address as it is shown and, for an IP check, sent to the API.
  target: string;
  type: 'ip' | 'proxy';
  proxy: { host: string; port: number } | null;
}

export interface WorkerOption {
  value: string;
  label: string;
  disabled?: boolean;
}

// A single check run on demand, outside the queue, from a vantage point the
// operator picks. The modal owns the whole run: setup → progress → result.
const QuickCheckModal: React.FC<{
  open: boolean;
  serverKey: string;
  target: QuickCheckTarget | null;
  workerOptions: WorkerOption[];
  onClose: () => void;
}> = ({ open, serverKey, target, workerOptions, onClose }) => {
  const { token } = theme.useToken();
  const [workerId, setWorkerId] = useState('');
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkedBy, setCheckedBy] = useState<{ workerName?: string; workerIP?: string; workerCountry?: string } | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopProgressTimer = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  // Opening the modal on a target is a fresh run: the previous result must not
  // be read as this target's. The chosen worker survives — it is a preference,
  // not a result.
  // Before paint, so the previous run never flashes under the new title.
  const targetKey = target ? `${target.type}:${target.target}` : '';
  useLayoutEffect(() => {
    if (!open) return;
    stopProgressTimer();
    setStarted(false);
    setLoading(false);
    setStep(0);
    setElapsed(0);
    setResult(null);
    setError(null);
    setCheckedBy(null);
  }, [open, targetKey, stopProgressTimer]);

  useEffect(() => stopProgressTimer, [stopProgressTimer]);

  const startProgressTimer = (type: 'ip' | 'proxy') => {
    stopProgressTimer();
    const startTime = Date.now();
    // Proxy step thresholds: 0=connect(~5s), 1=request(~10s), 2=latency(~2s)
    const proxyStepThresholds = [0, 5000, 15000];
    timer.current = setInterval(() => {
      const ms = Date.now() - startTime;
      setElapsed(ms);
      if (type === 'proxy') {
        let current = 0;
        for (let i = proxyStepThresholds.length - 1; i >= 0; i--) {
          if (ms >= proxyStepThresholds[i]) { current = i; break; }
        }
        setStep(current);
      }
    }, 200);
  };

  const runQuickCheck = async () => {
    if (!target) return;
    setStarted(true);
    setStep(0);
    setElapsed(0);
    setResult(null);
    setError(null);
    setCheckedBy(null);
    setLoading(true);
    startProgressTimer(target.type);
    try {
      const body: any = { serverKey, type: target.type };
      if (target.type === 'ip') {
        body.ip = target.target;
      } else if (target.proxy) {
        body.proxyHost = target.proxy.host;
        body.proxyPort = target.proxy.port;
      }
      if (workerId) body.workerId = workerId;

      const { data } = await apiClient.post('/health/quick-check', body);
      // The response is a DiagResult wrapper: payload in .data, the worker
      // vantage point (workerName/IP/Country) alongside it.
      const diag = data.data;
      const payload = diag?.data;
      const results = payload?.results || payload;
      setResult(Array.isArray(results) ? results : [results]);
      setCheckedBy(diag ?? null);
    } catch (err: any) {
      const errBody = err.response?.data;
      const errResults = errBody?.data?.data?.results || errBody?.data?.results;
      if (Array.isArray(errResults) && errResults.length > 0) {
        setResult(errResults);
      } else {
        setError(errBody?.error || err.message || 'Quick check failed');
      }
    } finally {
      stopProgressTimer();
      setLoading(false);
    }
  };

  const close = () => { stopProgressTimer(); onClose(); };
  const targetName = target?.target || '';
  const type = target?.type || 'ip';

  return (
    <Modal
      title={
        <Space size={4}>
          <ThunderboltOutlined />
          {`Quick Check — ${targetName}`}
        </Space>
      }
      open={open}
      onCancel={close}
      footer={
        loading
          ? <Button size="small" onClick={close}>Cancel</Button>
          : !started
            ? (
              <Space size={8}>
                <Button size="small" onClick={close}>Cancel</Button>
                <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={runQuickCheck}>Run Check</Button>
              </Space>
            )
            : (
              <Space size={8}>
                <Button size="small" icon={<PlayCircleOutlined />} onClick={runQuickCheck}>Run Again</Button>
                <Button size="small" type="primary" onClick={close}>Close</Button>
              </Space>
            )
      }
      width={620}
      destroyOnHidden
    >
      {/* === SETUP: pick the vantage point before running === */}
      {!started && !loading && (
        <Space direction="vertical" size={8} style={{ width: '100%', padding: '8px 0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {type === 'ip'
              ? <>Sends 3 ICMP packets to <Text code>{targetName}</Text>, bypassing the check queue.</>
              : <>Runs the full proxy cascade (TCP connect, HTTP request, latency) against <Text code>{targetName}</Text>, bypassing the check queue.</>}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>Run from worker</Text>
            <Select
              size="small"
              value={workerId}
              onChange={setWorkerId}
              options={workerOptions}
              style={{ width: '100%' }}
            />
          </div>
        </Space>
      )}

      {/* === PROGRESS DISPLAY === */}
      {loading && type === 'ip' && (
        <div style={{ padding: '16px 0' }}>
          <Steps
            direction="vertical"
            size="small"
            current={0}
            items={[
              {
                title: <Text strong>ICMP Ping</Text>,
                description: (
                  <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 12 }}>Sending 3 ICMP packets to <Text code>{targetName}</Text></Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{(elapsed / 1000).toFixed(1)}s elapsed</Text>
                  </Space>
                ),
                icon: <LoadingOutlined style={{ color: token.colorPrimary }} />,
              },
            ]}
          />
        </div>
      )}

      {loading && type === 'proxy' && (
        <div style={{ padding: '16px 0' }}>
          <Steps
            direction="vertical"
            size="small"
            current={step}
            items={[
              {
                title: <Text strong>TCP Connect</Text>,
                description: step === 0
                  ? <Text style={{ fontSize: 12 }}>Connecting to <Text code>{targetName}</Text>...</Text>
                  : <Text type="secondary" style={{ fontSize: 12 }}>Connected</Text>,
                icon: step === 0
                  ? <LoadingOutlined style={{ color: token.colorPrimary }} />
                  : <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
                status: step === 0 ? 'process' : 'finish',
              },
              {
                title: <Text strong>HTTP Request</Text>,
                description: step === 1
                  ? <Text style={{ fontSize: 12 }}>Requesting targets through proxy (google.com, baidu.com, detectportal.firefox.com)...</Text>
                  : step > 1
                    ? <Text type="secondary" style={{ fontSize: 12 }}>Targets checked</Text>
                    : <Text type="secondary" style={{ fontSize: 12 }}>Waiting...</Text>,
                icon: step === 1
                  ? <LoadingOutlined style={{ color: token.colorPrimary }} />
                  : step > 1
                    ? <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                    : undefined,
                status: step === 1 ? 'process' : step > 1 ? 'finish' : 'wait',
              },
              {
                title: <Text strong>Latency Measurement</Text>,
                description: step === 2
                  ? <Text style={{ fontSize: 12 }}>Measuring response time...</Text>
                  : <Text type="secondary" style={{ fontSize: 12 }}>Waiting...</Text>,
                icon: step === 2
                  ? <LoadingOutlined style={{ color: token.colorPrimary }} />
                  : undefined,
                status: step === 2 ? 'process' : 'wait',
              },
            ]}
          />
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{(elapsed / 1000).toFixed(1)}s elapsed</Text>
          </div>
        </div>
      )}

      {/* === VANTAGE POINT === */}
      {!loading && started && checkedBy?.workerName && (
        <Alert
          type="info"
          banner
          style={{ marginBottom: 12 }}
          message={
            <Space size={4}>
              <GlobalOutlined />
              <Text style={{ fontSize: 12 }}>
                Checked from: <Text strong style={{ fontSize: 12 }}>
                  {checkedBy.workerName}
                  {checkedBy.workerCountry ? ` · ${checkedBy.workerCountry}` : ''}
                  {checkedBy.workerIP ? ` · ${checkedBy.workerIP}` : ''}
                </Text>
              </Text>
            </Space>
          }
        />
      )}

      {/* === ERROR === */}
      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Tag color="red" style={{ fontSize: 13, padding: '2px 14px', marginBottom: 12 }}>FAILED</Tag>
          <Card size="small" style={{ borderColor: token.colorError }}>
            <Text type="danger" style={{ fontSize: 12 }}>{error}</Text>
          </Card>
        </div>
      )}

      {/* === RESULTS: IP PING === */}
      {!loading && Array.isArray(result) && result.length > 0 && type === 'ip' && (() => {
        const r = result[0];
        return (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <Tag color={r.success ? 'green' : 'red'} style={{ fontSize: 13, padding: '2px 14px' }}>
                {r.success ? 'REACHABLE' : 'UNREACHABLE'}
              </Tag>
            </div>

            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Target">{r.target || targetName}</Descriptions.Item>
              <Descriptions.Item label="Duration">{r.durationMs ?? '--'} ms</Descriptions.Item>
              <Descriptions.Item label="Latency">
                {r.latencyMs > 0
                  ? <Text strong style={{ color: r.latencyMs > 200 ? token.colorWarning : token.colorSuccess }}>{r.latencyMs.toFixed(1)} ms</Text>
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Packet Loss">
                <Text strong style={{ color: (r.packetLoss ?? 100) > 0 ? token.colorError : token.colorSuccess }}>
                  {r.packetLoss != null ? `${r.packetLoss.toFixed(0)}%` : '--'}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {r.error && (
              <Card size="small" style={{ marginTop: 8, borderColor: token.colorError }}>
                <Text type="danger" style={{ fontSize: 12 }}>{r.error}</Text>
              </Card>
            )}
          </div>
        );
      })()}

      {/* === RESULTS: PROXY === */}
      {!loading && Array.isArray(result) && result.length > 0 && type === 'proxy' && (() => {
        const r = result[0];
        return (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <Tag color={STATUS_COLORS[r.state] || 'default'} style={{ fontSize: 13, padding: '2px 14px' }}>
                {r.state}
              </Tag>
            </div>

            <Descriptions bordered column={2} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Proxy">{r.proxy}</Descriptions.Item>
              <Descriptions.Item label="Protocol">
                <Tag style={{ margin: 0 }}>{(r.protocol || '').toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Latency">
                {r.latencyMs > 0
                  ? <Text strong style={{ color: r.latencyMs > 5000 ? token.colorError : r.latencyMs > 2000 ? token.colorWarning : token.colorSuccess }}>{r.latencyMs.toFixed(1)} ms</Text>
                  : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Total Duration">
                <Text strong>{r.durationMs} ms</Text>
              </Descriptions.Item>
            </Descriptions>

            {r.error && (
              <Card size="small" style={{ marginBottom: 12, borderColor: token.colorError }}>
                <Text type="danger" style={{ fontSize: 12 }}>{r.error}</Text>
              </Card>
            )}

            {r.steps && r.steps.length > 0 && (
              <>
                <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Check Steps</Text>
                <Steps
                  direction="vertical"
                  size="small"
                  items={r.steps.map((step: any) => ({
                    title: (
                      <Space size={4}>
                        <Text strong style={{ fontSize: 13 }}>{STEP_LABELS[step.name] || step.name}</Text>
                        {STEP_DESCRIPTIONS[step.name] && (
                          <Tooltip title={STEP_DESCRIPTIONS[step.name]}>
                            <InfoCircleOutlined style={{ color: token.colorTextTertiary, fontSize: 12, cursor: 'help' }} />
                          </Tooltip>
                        )}
                        {step.latency > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{step.latency.toFixed(1)} ms</Text>}
                      </Space>
                    ),
                    description: (
                      <div>
                        {step.error
                          ? <Text type="danger" style={{ fontSize: 12 }}>{step.error}</Text>
                          : <Text type="secondary" style={{ fontSize: 12 }}>OK</Text>}
                        {Array.isArray(step.notes) && step.notes.length > 0 && (
                          <div style={{ marginTop: 2 }}>
                            {step.notes.map((n: string, i: number) => (
                              <Text
                                key={i}
                                style={{
                                  display: 'block',
                                  fontSize: 12,
                                  color: step.severity === 'warn' ? token.colorWarning : step.severity === 'info' ? token.colorInfo : token.colorTextTertiary,
                                }}
                              >
                                {n}
                              </Text>
                            ))}
                          </div>
                        )}
                        {step.targetResults && step.targetResults.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            {step.targetResults.map((tr: any, idx: number) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                {tr.success
                                  ? <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 12 }} />
                                  : <CloseCircleOutlined style={{ color: token.colorError, fontSize: 12 }} />}
                                <Text code style={{ fontSize: 12 }}>{tr.url}</Text>
                                {tr.latency > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{tr.latency.toFixed(0)} ms</Text>}
                                {tr.contentValidated && <Tag color="green" style={{ fontSize: 12, lineHeight: '16px', padding: '0 4px', margin: 0 }}>Content OK</Tag>}
                                {tr.contentMismatch && <Tag color="red" style={{ fontSize: 12, lineHeight: '16px', padding: '0 4px', margin: 0 }}>Content Mismatch</Tag>}
                                {tr.error && <Text type="danger" style={{ fontSize: 12 }}>{tr.error}</Text>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                    status: (step.success
                      ? (step.severity === 'warn' ? 'process' : 'finish')
                      : 'error') as 'finish' | 'error' | 'process',
                    icon: (() => {
                      if (!step.success) return <CloseCircleOutlined style={{ color: token.colorError }} />;
                      if (step.severity === 'warn') return <ExclamationCircleOutlined style={{ color: token.colorWarning }} />;
                      if (step.severity === 'info') return <InfoCircleOutlined style={{ color: token.colorPrimary }} />;
                      return <CheckCircleOutlined style={{ color: token.colorSuccess }} />;
                    })(),
                  }))}
                />
              </>
            )}
          </div>
        );
      })()}
    </Modal>
  );
};

export default QuickCheckModal;
